import { Component, EventEmitter, Input, Output, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserProvider } from 'ethers';
import { createAppKit } from '@reown/appkit';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import UniversalProvider from '@walletconnect/universal-provider';
import * as net from '@reown/appkit/networks'
import * as networks from '@reown/appkit/networks';
import SignClient from "@walletconnect/sign-client";
// Ces symboles sont fournis par ton intégration AppKit/WalletConnect existante.
// On les déclare ici pour TypeScript, sans en changer l'implémentation.
let modal: ReturnType<typeof createAppKit> | undefined;
let universalProvider: any | undefined;
let appKitInitPromise: Promise<void> | null = null;

function purgeWalletConnectStorage(addLog?: (s: string) => void, tag: string = 'purge') {
    try {
        const ssKeys: string[] = [];
        const lsKeys: string[] = [];
    
          for (let i = 0; i < sessionStorage.length; i++) {
              const k = sessionStorage.key(i);
              if (k) ssKeys.push(k);
            }
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k) lsKeys.push(k);
          }
    
          const shouldNuke = (k: string) => {
              const kk = k.toLowerCase();
              return (
                  kk.startsWith('wc@2') ||
                  kk.includes('walletconnect') ||
                  kk.includes('w3m') ||
                  kk.includes('reown') ||
                  kk.includes('appkit') ||
                  kk.includes('wagmi') ||
                 kk.startsWith('st/lastwc') ||
                  kk.startsWith('st/allowwc') ||
                  kk.startsWith('st/legacy')
                );
            };
    
          let removed = 0;
        ssKeys.forEach(k => {
            if (shouldNuke(k)) {
                try { sessionStorage.removeItem(k); removed++; } catch { }
              }
          });
        lsKeys.forEach(k => {
            if (shouldNuke(k)) {
                try { localStorage.removeItem(k); removed++; } catch { }
              }
          });
    
          addLog?.(`[WALLET][${tag}] purge storage done: removed=${removed}`);
      } catch (e: any) {
          addLog?.(`[WALLET][${tag}] purge storage failed: ${e?.message || e}`);
        }
  }

function safeJson(obj: any, depth: number = 2): string {
  const seen = new WeakSet();
  const recur = (value: any, level: number): any => {
    if (value === null || typeof value !== 'object') return value;
    if (seen.has(value)) return '[Circular]';
    if (level <= 0) return '[MaxDepth]';
    seen.add(value);

    if (Array.isArray(value)) {
      return value.slice(0, 10).map(v => recur(v, level - 1));
    }

    const out: any = {};
    Object.keys(value).slice(0, 20).forEach(k => {
      try {
        out[k] = recur((value as any)[k], level - 1);
      } catch {
        out[k] = '[Error]';
      }
    });
    return out;
  };

  try {
    return JSON.stringify(recur(obj, depth), null, 2);
  } catch {
    return '[Unserializable]';
  }
}

function initAppKit(projectId: string) {
  // ✅ On ne déclenche l'init qu'une seule fois
  if (!appKitInitPromise) {
    appKitInitPromise = (async () => {
      if (modal && universalProvider) {
        return;
      }

      console.log('[APPKIT][WC] init UniversalProvider…');

      // 1) UniversalProvider côté WalletConnect
      universalProvider = await (UniversalProvider as any).init({
        projectId,
        metadata: {
          name: 'SwapTokens',
          description: 'Swap & Bridge',
          url: 'https://swaptokens-zeta.vercel.app',
          icons: ['https://swaptokens-zeta.vercel.app/assets/icon-512.png']
        }
      });

      // 2) Sonde display_uri → callback globale (gérée côté SwapComponent)
      universalProvider.on?.('display_uri', (uri: string) => {
        const safe = uri || '';

        try {
          console.log(
            '[APPKIT][WC] display_uri',
            safe ? safe.slice(0, 120) + '…' : '(empty)'
          );
        } catch { /* no-op */ }

        // 🧪 DUMP SAFE de l’état WC autour du display_uri
        try {
          console.log(
            '[APPKIT][WC] display_uri SAFE snapshot',
            safeJson({
              uri: safe,
              providerKeys: Object.keys(universalProvider || {}),
              clientKeys: Object.keys((universalProvider as any)?.client || {}),
              coreKeys: Object.keys((universalProvider as any)?.client?.core || {}),
              pairing: (universalProvider as any)?.client?.core?.pairing,
              session: (universalProvider as any)?.client?.session,
            }, 2)
          );
        } catch (e) {
          console.warn('[APPKIT][WC] safeJson display_uri error', e);
        }

        // stash + callback comme avant
        try {
          (window as any)._appkit_last_display_uri = safe;
        } catch { /* no-op */ }

        try {
          (window as any).__onWcDisplayUri?.(safe);
        } catch (e) {
          console.error('[APPKIT][WC] __onWcDisplayUri error', e);
        }
      });



      // 3) AppKit Core branché sur UniversalProvider
      const adapter = new EthersAdapter();

      const config: any = {
        adapters: [adapter],
        networks: [
          {
            id: 1,
            name: 'Ethereum',
            rpcUrl:
              'https://eth-mainnet.g.alchemy.com/v2/gCvph7eowzBP0e2uUN177AELQnht-CXN'
          },
          {
            id: 137,
            name: 'Polygon',
            rpcUrl:
              'https://polygon-mainnet.g.alchemy.com/v2/gCvph7eowzBP0e2uUN177AELQnht-CXN'
          },
          {
            id: 56,
            name: 'BSC',
            rpcUrl: 'https://bsc-dataseed1.ninicoin.io'
          },
          {
            id: 42161,
            name: 'Arbitrum One',
            rpcUrl:
              'https://arb-mainnet.g.alchemy.com/v2/gCvph7eowzBP0e2uUN177AELQnht-CXN'
          }
        ],
        projectId,
        metadata: {
          name: 'SwapTokens',
          description: 'Swap & Bridge',
          url: 'https://swaptokens-zeta.vercel.app',
          icons: ['https://swaptokens-zeta.vercel.app/assets/icon-512.png']
        },
        features: {
          analytics: true,
          email: false,
          socials: false,
          onramp: false,
          swaps: false,
          send: false
        }
      };

      // 👉 pattern “AppKit Core + UniversalProvider + manualWCControl”
      config.walletConnect = {
        provider: universalProvider,
        manualWCControl: true
      };

      console.log('[APPKIT] init (Core + UniversalProvider)…');
      modal = createAppKit(config);

      (window as any).appKitModal = modal;
    })().catch(err => {
      console.error('[APPKIT][WC] init error', err);
      appKitInitPromise = null;
    });
  }
}



// ✅ KISS + robuste: garantir un modal utilisable AVANT d'appeler modal.open()
  // Bug root-cause: après HARD disconnect, on reset modal + appKitInitPromise.
  // Au clic "Connect", on faisait initAppKit() puis modal.open() sans attendre la fin de l'init async.
  // => crash: "undefined is not an object (evaluating 'modal.open')".
  async function ensureAppKitModalReady(projectId: string, ctx: string): Promise<any> {
      initAppKit(projectId);
    
        // Attendre l'init si elle est en cours (import/createAppKit async)
        if (appKitInitPromise) {
            try { await appKitInitPromise; } catch { /* initAppKit reset déjà appKitInitPromise */ }
          }
    
        const m: any = modal;
      if (!m || typeof m.open !== 'function') {
          throw new Error(`[APPKIT][modal] not ready after init | ctx=${ctx} hasModal=${!!m}`);
        }
      return m;
    }
  



let signClient: any;
async function initSignClient() {
  signClient = await SignClient.init({
    projectId: "ton_project_id_wc",
    relayUrl: "wss://relay.walletconnect.com",
    metadata: {
      name: "SwapTokens",
      description: "SwapTokens App",
      url: "https://swaptokens.app",
      icons: ["https://swaptokens.app/logo.png"],
    },
  });
}

// Helpers legacy (si tu veux les garder dans le composant)
// Tu peux aussi les retirer et laisser le parent gérer complètement la persistance.
declare function saveSimpleSession(data: { address: string; chainId: number; providerType: string; ts: number }): void;
declare function backupSession(): Promise<void>;

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-connect-wallet-legacy',
  templateUrl: './connect-wallet-legacy.component.html',
  styleUrls: ['./connect-wallet-legacy.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectWalletLegacyComponent implements OnDestroy {
  @Input() projectId = '8d74b0ff39c361ecaa9c3dd96ac0055c'; // le même que J-10
  @Output() connected = new EventEmitter<{ address: string; chainId: number; providerType: string; provider: any }>();
  @Output() disconnected = new EventEmitter<void>();
  @Input() takerAddress: string | null = null;
  @Input() addLog?: (msg: string) => void;
  @Input() dark: boolean | null = null;
  @Input() preferredChainId?: number;
  private log(msg: string, extra?: any) {
    const line = `[legacy] ${msg}${extra ? ' ' + JSON.stringify(extra) : ''}`;
    this.addLog?.(line);           // vers ton panneau debug
    console.log(line);             // et dans la console navigateur
  }
  constructor(private cdr: ChangeDetectorRef, private zone: NgZone) { }
  isConnected = false;
  address?: string;
  chainId?: number;
  provider?: BrowserProvider;

  private _rawProvider: any | null = null;
  private _healthInFlight = false;
  private _healthCooldownUntil = 0;
  private _onWinFocus = () => { void this._ensureStableConnected('window:focus'); };
  private _onVisChange = () => { void this._ensureStableConnected('visibilitychange'); };
  private _disconnectInFlight = false;
    // --- Apex strictness: connected ONLY if EIP-1193 is реально utilisable


      private purgeWalletConnectStorage(): void {
    const kill = (store: Storage) => {
        const keys: string[] = [];
        for (let i = 0; i < store.length; i++) {
            const k = store.key(i);
            if (k) keys.push(k);
          }
        const shouldDrop = (k: string) => {
            const kk = k.toLowerCase();
           return (
                kk.startsWith('wc@2:') ||
                kk.includes('walletconnect') ||
                kk.includes('wallet_connect') ||
                kk.includes('appkit') ||
                kk.includes('reown') ||
                kk.startsWith('wagmi') ||
                kk.startsWith('st/wc') ||
                kk.startsWith('st/last')
              );
          };
        keys.filter(shouldDrop).forEach((k) => {
            try { store.removeItem(k); } catch { }
          });
      };
    try { kill(localStorage); } catch { }
    try { kill(sessionStorage); } catch { }
  }

    private async _eip1193Healthcheck(rawProvider: any): Promise < { ok: boolean; chainId?: number; accountsLen?: number; err?: string } > {
    try {
        const hasReq = !!rawProvider?.request && typeof rawProvider.request === 'function';
        if (!hasReq) return { ok: false, err: 'no_request' };
  
          const chainHex = await this._withTimeout(
              rawProvider.request({ method: 'eth_chainId', params: [] }),
             2000,
              'eth_chainId'
            );
        const chainId = typeof chainHex === 'string' ? parseInt(chainHex, 16) : NaN;
        if (!Number.isFinite(chainId) || chainId <= 0) return { ok: false, err: `bad_chainId:${String(chainHex)}` };
  
         const accounts = await this._withTimeout(
              rawProvider.request({ method: 'eth_accounts', params: [] }),
              2000,
              'eth_accounts'
            );
        const accountsLen = Array.isArray(accounts) ? accounts.length : 0;
        // accounts peut être [] juste après pairing; on accepte len=0 mais on refuse si format invalide
          if (!Array.isArray(accounts)) return { ok: false, err: 'bad_accounts_format' };
  
          return { ok: true, chainId, accountsLen };
      } catch (e: any) {
          return { ok: false, err: e?.message || String(e) };
        }
  }

    private _markDisconnected(reason: string, extra ?: any) {
      this.addLog?.('[legacy] HARD-DOWN: not connected (bastard state)');
      this.isConnected = false;
      this.address = undefined;
      this.chainId = undefined;
      this.provider = undefined;
      this._rawProvider = null;
      try { this.disconnected.emit(); } catch { }
  }

    private _sleep(ms: number): Promise < void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  private async _withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise < T > {
 let t: any;
    const timeout = new Promise<T>((_, rej) => {
        t = setTimeout(() => rej(new Error(`timeout:${label}:${ms}ms`)), ms);
      });
    try {
        return await Promise.race([p, timeout]) as T;
      } finally {
        clearTimeout(t);
      }
  }

     // RADICAL: si "connected" mais provider inutilisable -> UI disconnect immédiat
  private async _ensureStableConnected(reason: string): Promise < boolean > {
    try {
        if (!this.isConnected) return false;
  
          const now = Date.now();
        if (now < this._healthCooldownUntil) return true;
        if (this._healthInFlight) return true;
  
          this._healthInFlight = true;
  
          const rp = this._rawProvider ?? (this as any)?.provider?.provider ?? null;
        const hasReq = !!rp && typeof rp?.request === 'function';
        const addr = (this.address || '').toLowerCase();
        const cid = (this.chainId ?? null);
  
          this.addLog?.(`[legacy][stable] check ENTER | reason=${reason} hasRaw=${!!rp} hasReq=${hasReq} addr=${addr || '(empty)'} cid=${cid ?? '(null)'}`);
  
          // FAST FAIL: pas de provider EIP-1193 => état bâtard => disconnect UI
          if (!rp || !hasReq || !addr || !cid) {
              this.addLog?.(`[legacy][stable] FAIL fast | reason=${reason} -> force disconnect (missing raw/req/addr/cid)`);
              this._markDisconnected(`unstable:${reason}:fast`);
              return false;
            }
  
          // HEALTHCHECK strict (chainId + accounts)
          const hc = await this._withTimeout(this._eip1193Healthcheck(rp), 1500, `eip1193Healthcheck:${reason}`);
        this._healthCooldownUntil = Date.now() + 5000;
  
          if (!hc?.ok || !hc.chainId || (hc.accountsLen ?? 0) <= 0) {
              this.addLog?.(`[legacy][stable] FAIL hc | reason=${reason} hc=${JSON.stringify(hc)}`);
              this._markDisconnected(`unstable:${reason}:healthcheck`, hc);
              return false;
            }
  
         // si mismatch chain => état incohérent => disconnect UI
          if (cid && hc.chainId && cid !== hc.chainId) {
              this.addLog?.(`[legacy][stable] FAIL chain mismatch | reason=${reason} ui=${cid} provider=${hc.chainId}`);
              this._markDisconnected(`unstable:${reason}:chain-mismatch`, { ui: cid, provider: hc.chainId });
              return false;
            }
  
          this.addLog?.(`[legacy][stable] OK | reason=${reason}`);
        return true;
      } catch (e: any) {
          this.addLog?.(`[legacy][stable] FAIL exception | reason=${reason} err=${e?.message || e}`);
          this._markDisconnected(`unstable:${reason}:exception`, e?.message || e);
          return false;
        } finally {
        this._healthInFlight = false;
      }
 }

    // Stale session recovery (anti "session exists but provider none")
  private _resettingStaleSession = false;
  private readonly STALE_RESET_COOLDOWN_MS = 12_000;
  private readonly STALE_GRACE_MS = 2_000;



  isDarkMode(): boolean {
    // Priorité à l'info venant du parent
    if (this.dark !== null && this.dark !== undefined) {
      return this.dark;
    }
    // Fallback sécurité
    return document.body.classList.contains('dark-mode');
  }

  getFormattedAddress(address: string | null): string {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }


    private isWcSessionUsable(session: any): boolean {
      try {
          if (!session) return false;
          const accounts: string[] = session?.namespaces?.eip155?.accounts ?? [];
          if (!Array.isArray(accounts) || accounts.length === 0) return false;
          return accounts.some(a => typeof a === 'string' && a.startsWith('eip155:'));
        } catch {
            return false;
          }
    }

    private isProviderReadyAny(up: any): boolean {
      try {
          const p1 = up && typeof up.request === 'function';
          const p2 = up?.provider && typeof up.provider.request === 'function';
          return !!(p1 || p2);
        } catch {
            return false;
          }
    }

    private canResetStaleNow(): boolean {
      try {
          const raw = sessionStorage.getItem('st/lastStaleResetAtMs') || '0';
          const last = Number(raw) || 0;
          return Date.now() - last >= this.STALE_RESET_COOLDOWN_MS;
        } catch {
            return true;
          }
    }

    private markStaleResetNow(): void {
    try {
        sessionStorage.setItem('st/lastStaleResetAtMs', String(Date.now()));
      } catch { }
  }

    private async resetStaleWcSession(ctx: string): Promise < void> {
    if (this._resettingStaleSession) {
        this.log(`[wc][reset] skip: already resetting`, { ctx });
        return;
      }
   if (!this.canResetStaleNow()) {
        this.log(`[wc][reset] denied: cooldown`, { ctx });
        return;
      }

      this._resettingStaleSession = true;
    const t0 = Date.now();
   try {
        this.log(`[wc][reset] ENTER`, { ctx });
  
          // 1) disconnect session si possible (best effort)
          try {
              const s = (universalProvider as any)?.session;
              if (s) {
                  this.log(`[wc][reset] disconnecting session`, { topic: (s as any)?.topic ?? null });
                  await (universalProvider as any)?.disconnect?.();
                } else {
                  this.log(`[wc][reset] no session to disconnect`);
                }
            } catch (e: any) {
                this.log(`[wc][reset] disconnect error (ignored)`, { err: String(e?.message ?? e) });
              }
  
          // 2) purge cached uris / state (no deeplink)
          try { sessionStorage.removeItem('st/lastWcUriRaw'); } catch { }
        try { sessionStorage.removeItem('st/lastWcDisplayUri'); } catch { }
        try { (window as any)._wcLastUri = ''; } catch { }
        try { (window as any)._appkit_last_display_uri = ''; } catch { }
  
          // 3) empêche auto-restore immédiat qui reboucle
          try { sessionStorage.setItem('st/skipAutoRestoreOnce', '1'); } catch { }
  
          this.markStaleResetNow();
        this.log(`[wc][reset] EXIT`, { inMs: Date.now() - t0 });
      } finally {
        this._resettingStaleSession = false;
      }
  }

  private installAppKitModalDebugHooks(reason: string): void {
    const w: any = window as any;
    const m: any = w?.appKitModal;

    this.addLog?.(`[wc][modal-debug] install | reason=${reason} hasModal=${!!m} hasSub=${typeof m?.subscribeProviders} hasGet=${typeof m?.getWalletProvider}`);

    if (!m) return;

    // 1) Wrap close() to know if it's even called
    try {
      if (typeof m.close === 'function' && !(m as any).__apexCloseWrapped) {
        const origClose = m.close.bind(m);
        (m as any).__apexCloseWrapped = true;
        m.close = (...args: any[]) => {
          this.addLog?.(`[wc][modal-debug] modal.close() CALLED | argsLen=${args?.length ?? 0}`);
          try { return origClose(...args); }
          catch (e: any) { this.addLog?.(`[wc][modal-debug] modal.close() THROW | ${e?.message || e}`); throw e; }
        };
        this.addLog?.(`[wc][modal-debug] close() wrapped`);
      }
    } catch { }

    // 2) Subscribe tick visibility: does callback ever fire?
    try {
      if (typeof m.subscribeProviders === 'function' && !(m as any).__apexSubWrapped) {
        const origSub = m.subscribeProviders.bind(m);
        (m as any).__apexSubWrapped = true;

        m.subscribeProviders = (...args: any[]) => {
          this.addLog?.(`[wc][modal-debug] subscribeProviders() called | argsLen=${args.length}`);
          const t0 = Date.now();

          // If first arg is callback, wrap it to log each tick
          if (typeof args[0] === 'function') {
            const cb0 = args[0];
            args[0] = (state: any) => {
              const keys = state ? Object.keys(state) : [];
              const eip = state?.eip155;
              const eipKeys = eip ? Object.keys(eip) : [];
              const raw =
                eip?.provider ||
                eip?.rpcProviders?.eip155 ||
                null;

              this.addLog?.(
                `[wc][modal-debug] subscribe tick | +${Date.now() - t0}ms keys=${keys.join(',')} eipKeys=${eipKeys.join(',')}` +
                ` rawProvider=${!!raw} rawHasReq=${typeof raw?.request === 'function'}`
              );

              try { return cb0(state); } catch (e: any) {
                this.addLog?.(`[wc][modal-debug] subscribe tick cb THROW | ${e?.message || e}`);
              }
            };
          }

          const r = origSub(...args);

          // log return type
          this.addLog?.(
            `[wc][modal-debug] subscribeProviders() returned | type=${typeof r} thenable=${!!(r && typeof r.then === 'function')}`
          );

          return r;
        };

        this.addLog?.(`[wc][modal-debug] subscribeProviders() wrapped`);
      }
    } catch (e: any) {
      this.addLog?.(`[wc][modal-debug] subscribe wrap error | ${e?.message || e}`);
    }

    // 3) Poll getWalletProvider in background (logs only)
    try {
      if (!(m as any).__apexGetPoll) {
        (m as any).__apexGetPoll = true;
        let tick = 0;
        const poll = async () => {
          tick++;
          try {
            const maybe = (() => { try { return m.getWalletProvider?.('eip155'); } catch { return null; } })();
            const p0 = (maybe && typeof maybe.then === 'function') ? await maybe : maybe;

            const hasReq = typeof p0?.request === 'function';
            const hasUri = typeof p0?.uri === 'string';
            this.addLog?.(`[wc][modal-debug] getWalletProvider poll#${tick} | got=${!!p0} hasReq=${hasReq} hasUri=${hasUri}`);

          } catch (e: any) {
            this.addLog?.(`[wc][modal-debug] getWalletProvider poll#${tick} THROW | ${e?.message || e}`);
          }
          if (tick < 30) window.setTimeout(poll, 1000);
        };
        window.setTimeout(poll, 250);
        this.addLog?.(`[wc][modal-debug] getWalletProvider poll armed`);
      }
    } catch { }
  }

  // ——————————————————————————————————————————————————————
  // Version "J-10" : simple, sans guards/runId, sans deeplink gate
  // ——————————————————————————————————————————————————————
  async connectWallet(): Promise<void> {
    try {
      const targetChainId = Number(sessionStorage.getItem('st/desiredChainId') || this.chainId || 1);
      // 1) Contexte / env
      this.log('connect START', {
        ua: navigator.userAgent,
        platform: (navigator as any).platform,
        visibility: document.visibilityState,
        focus: document.hasFocus(),
        projectId: this.projectId,
        targetChainId
      });
      // Empêche l’auto-restore pendant que l’utilisateur initie un connect
      sessionStorage.setItem('st/skipAutoRestoreOnce', '1');
      sessionStorage.setItem('st/legacyConnect', '1')
      this.log('flag set', { key: 'st/skipAutoRestoreOnce', value: 1 });
           // ✅ IMPORTANT: après un HARD disconnect, initAppKit() est async.
              // On attend ici pour garantir que `modal` existe vraiment (sinon modal.open crash)
              const m = await ensureAppKitModalReady(this.projectId, 'connectWallet');
           this.log('initAppKit ensured');


      // ✅ Autorise le deeplink et OUVRE LA MODALE IMMÉDIATEMENT (garde le "user gesture")
      (window as any).__allowWcDeeplinkFor?.(15_000);
      this.log('allow deeplink window', { ms: 15000 });
      this.log('modal.open Connect(eip155) →');
      // ✅ Autorise le deeplink (30s) AVANT d’ouvrir la modale
      const until = Date.now() + 30_000;          // 30s conseillé sur mobile
      sessionStorage.setItem('st/allowWcDeeplinkUntil', String(until));
      // Astuce: mets 0 pour matcher ta règle "ALLOW (window active) quand runId === 0"
      sessionStorage.setItem('st/allowWcRunId', '0');
      sessionStorage.removeItem('st/skipWcDeeplinkOnce'); // au cas où

      this.log('[deeplink][allow]', { until, runId: 0 });

      // (optionnel) keep-alive tant que la modale est ouverte
      const hb = setInterval(() => {
        const bump = Date.now() + 6_000;
        try { sessionStorage.setItem('st/allowWcDeeplinkUntil', String(bump)); } catch { }
      }, 5_000);

      this.log('[modal] about to open', {
        vis: document.visibilityState,
        focus: document.hasFocus(),
        allowUntil: sessionStorage.getItem('st/allowWcDeeplinkUntil'),
        runId: sessionStorage.getItem('st/allowWcRunId'),
        skipOnce: sessionStorage.getItem('st/skipWcDeeplinkOnce'),
        lastRaw: (window as any)._wcLastUri ? `${(window as any)._wcLastUri.slice(0, 60)}…` : '(empty)',
        lastDisplay: (window as any)._appkit_last_display_uri ? 'yes' : 'no'
      });
      this.installAppKitModalDebugHooks('before-open');
      // === WC SURFACING TIMER START ===
      const wcSurfacingT0 = Date.now();
      (window as any).__wcSurfacingT0 = wcSurfacingT0;

      this.log?.(`[WC][SURFACING] start | t0=${wcSurfacingT0}`);

      // IMPORTANT: ne pas mettre "await" ici pour ne pas casser le gesture
      m.open({ view: 'Connect', namespace: 'eip155' });

      this.installAppKitModalDebugHooks('after-open');
      this.log('[modal] open returned (sync)');

      this.log('[modal] subscribeProviders typeof', {
 type: typeof (modal as any)?.subscribeProviders
      });

      
              // ==========================================================
              // ✅ POINT 2: MANUAL MODE — we initiate pairing ourselves
              // Goal: force WalletConnect to emit display_uri quickly
              // ==========================================================
              try {
                  const w: any = window as any;
                 if (w._wc_pairing_inflight) {
                      this.log('[pairing] skip: inflight already true');
                    } else {
                      w._wc_pairing_inflight = true;
                      const lastDisplay = w._appkit_last_display_uri;
                      const lastRaw = w._wcLastUri;
                      const hasSession =
                          !!(universalProvider as any)?.session ||
                          !!(universalProvider as any)?.client?.session?.length;
            
                        this.log('[pairing] start (manual)', {
 hasUniversalProvider: !!universalProvider,
                            hasSession,
                            lastDisplay: lastDisplay ? 'yes' : 'no',
                            lastRaw: lastRaw ? 'yes' : 'no',
                          });
          
                      // Do NOT await in the main flow (keep UX responsive)
                      (async () => {
                          const tPair0 = Date.now();
                          try {
                              // Ensure init finished (best effort)
                                try { await appKitInitPromise; } catch { }
                
                                if (!universalProvider) {
                                    this.log('[pairing] abort: universalProvider missing');
                                    return;
                                  }
                

                                                       // If already has a session, do not create a new pairing
                                                        const sess = (universalProvider as any)?.session;
                                                       const hasSess =
                                                             !!sess ||
                                                            !!(universalProvider as any)?.client?.session?.length;
                       
                                                         const usableSession = this.isWcSessionUsable(sess);
                                                       const providerReady = this.isProviderReadyAny(universalProvider);
                       
                                                         this.log('[pairing] health', {
 hasSess,
                                                             usableSession,
                                                             providerReady
                                                           });
                     
                                                       // ⚠️ Stale-session recovery (ONLY in user-driven connect flow)
                                                       // Grace window to avoid false positives (slow provider exposure on iOS)
                                                       if (hasSess && (!usableSession || !providerReady)) {
                                                           this.log('[pairing] stale? waiting grace', { graceMs: this.STALE_GRACE_MS });
                                                           await new Promise(r => setTimeout(r, this.STALE_GRACE_MS));
                         
                                                             const sess2 = (universalProvider as any)?.session;
                                                           const hasSess2 =
                                                                 !!sess2 ||
                                                                !!(universalProvider as any)?.client?.session?.length;
                                                           const usable2 = this.isWcSessionUsable(sess2);
                                                           const ready2 = this.isProviderReadyAny(universalProvider);
                         
                                                             this.log('[pairing] stale recheck', {
 hasSess: hasSess2,
                                                                 usableSession: usable2,
                                                                 providerReady: ready2
                                                             });
                     
                                                         if (hasSess2 && (!usable2 || !ready2)) {
                                                             this.log('[pairing] stale confirmed -> reset');
                                                            await this.resetStaleWcSession('[pairing]');
                                                           }
                                                     }

                                                     // If session still exists and looks usable -> skip
                                                     const sessFinal = (universalProvider as any)?.session;
                                                  const hasSessFinal =
                                                       !!sessFinal || !!(universalProvider as any)?.client?.session?.length;
                                                   if (
                                                       hasSessFinal &&
                                                       this.isWcSessionUsable(sessFinal) &&
                                                       this.isProviderReadyAny(universalProvider)
                                                     ) {
                                                      this.log('[pairing] skip: session already exists AND looks usable');
                                                       return;
                                                     }
                
                                // Request namespaces explicitly (EVM)
                                const requiredNamespaces: any = {
 eip155: {
                                    chains: [`eip155:${targetChainId}`],
                                    methods: [
                                        'eth_sendTransaction',
                                        'eth_signTransaction',
                                        'eth_sign',
                                        'personal_sign',
                                        'eth_signTypedData',
                                        'eth_signTypedData_v4',
                                        'wallet_switchEthereumChain',
                                        'wallet_addEthereumChain'
                                      ],
                                      events: ['chainChanged', 'accountsChanged']
                                      }
              };
                            this.log('[pairing] requiredNamespaces', { chains: requiredNamespaces?.eip155?.chains });
                          this.log('[pairing] connect() call', {
 chains: requiredNamespaces?.eip155?.chains,
                              methods: requiredNamespaces?.eip155?.methods?.length,
                            });
      
                      // This should trigger universalProvider 'display_uri'
                      await (universalProvider as any).connect({ namespaces: requiredNamespaces });
      
                      this.log('[pairing] connect() resolved', { inMs: Date.now() - tPair0 });
                  } catch (e: any) {
                      this.log('[pairing] connect() error', {
 msg: e?.message || String(e),
                          code: e?.code,
                        });
                } finally {
                    try { w._wc_pairing_inflight = false; } catch { }
                    this.log('[pairing] done', { inMs: Date.now() - tPair0 });
                  }
          }) ();
        }
      } catch (e: any) {
          this.log('[pairing] fatal error', { msg: e?.message || String(e) });
        }




      setTimeout(() => {
        this.log('[watchdog] +2500ms after open', {
          vis: document.visibilityState,
          focus: document.hasFocus(),
          lastRaw: (window as any)._wcLastUri ? `${(window as any)._wcLastUri.slice(0, 60)}…` : '(empty)',
          lastDisplay: (window as any)._appkit_last_display_uri ? 'yes' : 'no'
        });
      }, 2500);


      // (Optionnel) init WC en arrière-plan si tu veux le garder pour ailleurs
      // void initSignClient(); // pas nécessaire pour l'ouverture via AppKit

      // Attend le provider EIP-1193 exposé par AppKit
      let ticks = 0;
      const t0 = Date.now();
      const rawProvider: any = await new Promise((resolve) => {
        let done = false;
        const unsub = modal!.subscribeProviders?.((state: any) => {
          ticks++;
          if (ticks === 1 || ticks === 3 || ticks === 6) {
            this.log('[modal] subscribeProviders tick', { ticks });
          }

          try {
            const stateKeys = Object.keys(state || {});
            const evm = state?.['eip155'];
            const evmKeys = evm ? Object.keys(evm) : [];
            const p = evm?.provider ?? evm;
            const provKeys = p ? Object.keys(p) : [];

            // 🔍 Sondes debug pour voir si quelque chose ressemble à un wcUri
            const wcHints: any = {
              evm_type: evm ? typeof evm : '(null)',
              p_type: p ? typeof p : '(null)',
              evm_has_uri: !!(evm && ('uri' in evm)),
              prov_has_uri: !!(p && ('uri' in p)),
              evm_uri_preview: (evm as any)?.uri
                ? String((evm as any).uri).slice(0, 64)
                : undefined,
              prov_uri_preview: (p as any)?.uri
                ? String((p as any).uri).slice(0, 64)
                : undefined,
              prov_constructor: p?.constructor?.name,
            };

            this.log('subscribeProviders tick', {
              ticks,
              stateKeys,
              evmKeys,
              provKeys,
              wcHints,
            });
            // 🧪 SNAPSHOT SAFE COMPLET (state + evm + provider)
            this.log('subscribeProviders SAFE snapshot', {
              snapshot: safeJson(
                {
                  state,
                  evm,
                  provider: p,
                },
                2
              ),
            });

            // On garde un snapshot global pour inspection manuelle dans la console
            (window as any)._appkit_state_debug = {
              lastState: state,
              evm,
              provider: p,
            };
          } catch (e: any) {
            this.log('subscribeProviders tick error', { msg: e?.message || String(e) });
          }

          if (done) return;
          const evm = state?.['eip155'];
          if (!evm) return;
          const p = evm.provider ?? evm;
          if (p) {
            done = true;
            try { unsub?.(); } catch { }
            try { clearInterval(hb as any); } catch { }
            this.log('provider surfaced via AppKit', { hasRequest: typeof (p as any)?.request === 'function' });
            const t0 = (window as any).__wcSurfacingT0;
            if (t0) {
              const dt = Date.now() - t0;
              this.log?.(`[WC][SURFACING] DONE in ${dt}ms`);
              (window as any).__wcSurfacingT0 = null; // cleanup
            }


            // 🧪 Snapshot brut du provider pour voir s’il contient un wcUri
            try {
              (window as any)._appkit_rawProvider_debug = p;
              this.log('rawProvider snapshot', {
                ctor: p?.constructor?.name,
                hasRequest: typeof p?.request === 'function',
                keys: Object.keys(p || {}),
                has_uri: 'uri' in (p || {}),
                uri_preview: (p as any)?.uri
                  ? String((p as any).uri).slice(0, 80)
                  : undefined,
              });

              // 🧪 DUMP SAFE DU PROVIDER
              this.log('rawProvider SAFE dump', {
                snapshot: safeJson(
                  {
                    provider: p,
                  },
                  2
                ),
              });
            } catch (e: any) {
              this.log('rawProvider snapshot error', { msg: e?.message || String(e) });
            }

            // 🔗 NEW: extraire wcUri depuis le provider et le pousser vers le hook global
            try {
              const fullUri: string | undefined =
                typeof (evm as any)?.uri === 'string'
                  ? String((evm as any).uri)
                  : typeof (p as any)?.uri === 'string'
                    ? String((p as any).uri)
                    : undefined;

              if (fullUri) {
                this.log('wcUri captured from provider.uri', {
                  len: fullUri.length,
                  head: fullUri.slice(0, 80),
                });

                const w: any = window as any;
                // cache debug global
                w._wcLastUri = fullUri;

                const onDisplay = w.__onWcDisplayUri;
                if (typeof onDisplay === 'function') {
                  try {
                    onDisplay(fullUri);
                    this.log('wcUri forwarded via __onWcDisplayUri');
                  } catch (err: any) {
                    this.log('wcUri forward error', {
                      msg: err?.message || String(err),
                    });
                  }
                } else {
                  this.log(
                    '__onWcDisplayUri missing, wcUri only cached in window._wcLastUri',
                  );
                }
              } else {
                this.log(
                  'wcUri not found on provider / evm, nothing forwarded',
                );
              }
            } catch (err: any) {
              this.log('wcUri capture fatal error', {
                msg: err?.message || String(err),
              });
            }

            resolve(p);

          }
        });
      });

      this.log('subscribeProviders resolved', { inMs: Date.now() - t0 });
      const pType = modal!.getWalletProviderType?.();
      this.log('providerType', pType ?? 'unknown');
      if (typeof rawProvider?.request !== 'function') {
        this.log('⛔ Invalid EIP-1193 provider', { hasRequest: typeof rawProvider?.request });
        throw new Error('Invalid EIP-1193 provider');
      }

      this.provider = new BrowserProvider(rawProvider);
      this.log('BrowserProvider attached');

      // Ethers signer → adresse + chain
      const signer = await this.provider.getSigner();
      const address = await signer.getAddress();
      const { chainId } = await this.provider.getNetwork();
      this.log('signer/network', { address, chainId: Number(chainId) });

      // État local
      this.address = address;
      this.chainId = Number(chainId);
      this.isConnected = true;
      this.log('state updated', { isConnected: true });

      // Persistance simple (optionnelle — c’est le comportement J-10)
      try {
               const fn: any = (typeof (window as any).saveSimpleSession === 'function')
                   ? (window as any).saveSimpleSession
                 : (typeof (globalThis as any).saveSimpleSession === 'function')
                     ? (globalThis as any).saveSimpleSession
                   : null;
        
                 if (!fn) throw new Error("saveSimpleSession not found");
               fn({
          address,
          chainId: Number(chainId),
          providerType: pType ?? 'unknown',
          ts: Date.now(),
        });
        this.log('saveSimpleSession ok', { address, chainId: Number(chainId), providerType: pType ?? 'unknown' });
      } catch (e: any) {
        this.log('saveSimpleSession error', { err: e?.message || String(e) });
      }

      try {
               const fn2: any = (typeof (window as any).backupSession === 'function')
                   ? (window as any).backupSession
                 : (typeof (globalThis as any).backupSession === 'function')
                     ? (globalThis as any).backupSession
                   : null;
               if (!fn2) throw new Error("backupSession not found");
               await fn2();
        this.log('backupSession ok');
      } catch (e: any) {
        this.log('backupSession error', { err: e?.message || String(e) });

      }

      // Notifie le parent : il pourra setChain(), refreshTokens(), etc.
      this.connected.emit({
        address,
        chainId: Number(chainId),
        providerType: pType ?? 'unknown',
        provider: rawProvider,
      });
      this.log('connected emitted');
      // Fin: on lève le skip auto-restore
      sessionStorage.removeItem('st/skipAutoRestoreOnce');
      sessionStorage.removeItem('st/legacyConnect');
      this.log('flag removed', { key: 'st/skipAutoRestoreOnce' });
    } catch (err: any) {
      this.log('ERROR', { message: err?.message, code: err?.code, stack: typeof err?.stack === 'string' ? err.stack.slice(0, 300) : undefined });
      sessionStorage.removeItem('st/skipAutoRestoreOnce');
      sessionStorage.removeItem('st/legacyConnect');
      alert('Connexion échouée: ' + (err?.message || 'Unknown error'));
    }
  }
    private hardResetAppKitSingletons(ctx: string): void {
    const log = (m: string) => this.addLog?.(`[legacy] [appkit][reset] ${m}`);
    log(`ENTER ctx=${ctx} | modal=${!!modal} up=${!!universalProvider} hasInitPromise=${!!appKitInitPromise}`);

      // Best-effort shutdown (never throw)
      try { (modal as any)?.close?.(); } catch { }
    try { (modal as any)?.disconnect?.(); } catch { }
    try { (universalProvider as any)?.disconnect?.(); } catch { }

      // Atomic nuke
      try { (modal as any) = null; } catch { }
    try { (universalProvider as any) = null; } catch { }
      try { (appKitInitPromise as any) = null; } catch { }


      // Nettoyage window (évite des refs pendantes)
      try { (window as any).appKitModal = null; } catch { }

      // Optional: clear any app-level debug state that could re-trigger UI
      try { (window as any).__st_lastAppkitDisplayUri = undefined; } catch { }

      log(`DONE ctx=${ctx} | modal=${!!modal} up=${!!universalProvider} hasInitPromise=${!!appKitInitPromise}`);
 }
      async disconnectWallet(tag: string = 'manual'): Promise < void> {
    if (this._disconnectInFlight) {
        try { this.addLog?.(`[wallet] HARD disconnect ignored (in-flight) | tag=${tag}`); } catch { }
        return;
      }
    this._disconnectInFlight = true;
    const startedAt = Date.now();

      const withTimeout = async <T>(p: Promise<T>, ms: number, label: string): Promise<T | undefined> => {
          let t: any;
          try {
              return await Promise.race([
                  p,
                  new Promise<T | undefined>((resolve) => { t = setTimeout(() => resolve(undefined), ms); })
                ]);
            } finally {
              if (t) clearTimeout(t);
              try { this.addLog?.(`[wallet] timeoutGuard ${label} ${ms}ms`); } catch { }
            }
        };

      try {
          try { this.addLog?.(`[wallet] HARD disconnect start | tag=${tag}`); } catch { }
    
            // 0) close modal first (prevents iOS greyed/busy state on quick reopen)
            try { (modal as any)?.close?.(); } catch { }
    
            // 1) AppKit modal disconnect (guarded)
            try { await withTimeout((modal as any)?.disconnect?.(), 2500, 'modal.disconnect'); } catch { }
    
            // 2) WalletConnect UniversalProvider hard disconnect (best effort)
            try { await withTimeout((universalProvider as any)?.disconnect?.(), 2500, 'universalProvider.disconnect'); } catch { }
          try { await withTimeout((universalProvider as any)?.disconnectSession?.(), 2500, 'universalProvider.disconnectSession'); } catch { }
          try { await withTimeout((universalProvider as any)?.client?.disconnect?.(), 2500, 'universalProvider.client.disconnect'); } catch { }
    
            // 3) purge cached state to prevent instant auto-restore
            try { this.purgeWalletConnectStorage(); } catch { }
          try {
              const until = Date.now() + 15000;
              localStorage.setItem('st/supAutoRestoreUntil', String(until));
             localStorage.setItem('st/supWcUriUntil', String(until));
            } catch { }
    
          //  // 4) drop singletons to force clean init next connect
          //  try { (modal as any) = undefined; } catch { }
          //try { (universalProvider as any) = undefined; } catch { }
        //try { (appKitInitPromise as any) = null; } catch { }
        this.hardResetAppKitSingletons('disconnectWallet');
    
            // 5) reset UI state in Angular zone + OnPush refresh
            this.zone.run(() => {
                this.isConnected = false;
                this.address = undefined;
                this.chainId = undefined;
                this.provider = undefined;
                try { this.cdr.markForCheck(); } catch { }
              });
    
            try { this.disconnected.emit(); } catch { }
          try { this.addLog?.(`[wallet] HARD disconnect done in ${Date.now() - startedAt}ms`); } catch { }
        } catch (e: any) {
            try { this.addLog?.(`[wallet] HARD disconnect error: ${e?.message || e}`); } catch { }
            // no-op
            } finally {
        this._disconnectInFlight = false;
      }
  }

  async ngOnInit(): Promise<void> {
    if (!modal) initAppKit('8d74b0ff39c361ecaa9c3dd96ac0055c');

  }

  ngOnDestroy(): void {
    // rien à nettoyer côté legacy (pas de listeners persos)
  }
}
